import React, { useState } from 'react';
import { CloudIcon, FolderIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { useIntegracoes } from '../../hooks/useIntegracoes';

interface SeletorIntegracaoProps {
  onSelect: (integracaoId: string | null) => void;
  selectedId?: string | null;
  showLabel?: boolean;
  disabled?: boolean;
}

const SeletorIntegracao: React.FC<SeletorIntegracaoProps> = ({ 
  onSelect, 
  selectedId = null, 
  showLabel = true,
  disabled = false 
}) => {
  const { getIntegracoesAtivas, integracoes, loading } = useIntegracoes();
  const [isOpen, setIsOpen] = useState(false);

  const integracoesAtivas = getIntegracoesAtivas();
  const selectedIntegracao = integracoesAtivas.find(int => int.id === selectedId);

  // 🔍 DEBUG: Log detalhado do estado das integrações
  console.group('🔍 [SeletorIntegracao] Estado atual');
  console.log('⚙️ Loading:', loading);
  console.log('📊 Total de integrações:', integracoes?.length || 0);
  console.log('✅ Integrações ativas:', integracoesAtivas.length);
  console.log('📋 Lista completa de integrações:', integracoes?.map(i => ({
    id: i.id,
    nome: i.nome,
    tipo: i.tipo,
    ativo: i.ativo
  })) || []);
  console.log('📋 Lista de integrações ativas:', integracoesAtivas.map(i => ({
    id: i.id,
    nome: i.nome,
    tipo: i.tipo,
    ativo: i.ativo
  })));
  console.log('🔍 Props recebidas:', {
    selectedId,
    selectedIntegracao: selectedIntegracao?.nome,
    disabled
  });
  console.groupEnd();

  const handleSelect = (integracaoId: string | null) => {
    onSelect(integracaoId);
    setIsOpen(false);
  };

  if (loading) {
    return (
      <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-center text-blue-800">
          <CloudIcon className="h-4 w-4 mr-2 animate-spin" />
          <span className="text-sm">Carregando integrações...</span>
        </div>
      </div>
    );
  }

  if (integracoesAtivas.length === 0) {
    return (
      <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
        <div className="flex items-center text-yellow-800">
          <CloudIcon className="h-4 w-4 mr-2" />
          <span className="text-sm">
            🚨 DEBUG: Nenhuma integração configurada
          </span>
        </div>
        <div className="text-xs text-yellow-700 mt-2">
          Total: {integracoes?.length || 0} | Ativas: {integracoesAtivas.length} | Loading: {loading ? 'Sim' : 'Não'}
        </div>
        <div className="text-xs text-yellow-600 mt-1">
          Configure em <strong>Configurações → Integrações</strong>
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      {showLabel && (
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Integração para Anexo
        </label>
      )}
      
      <div className="relative">
        <button
          type="button"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          className={`w-full text-left bg-white border border-gray-300 rounded-lg px-3 py-2 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
            disabled ? 'bg-gray-50 cursor-not-allowed' : 'hover:bg-gray-50'
          }`}
        >
          {selectedIntegracao ? (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CloudIcon className="h-4 w-4 text-blue-500" />
                <div>
                  <span className="font-medium text-gray-900">
                    {selectedIntegracao.nome}
                  </span>
                  <div className="flex items-center gap-1 text-sm text-gray-500">
                    <FolderIcon className="h-3 w-3" />
                    {selectedIntegracao.pastaNome}
                  </div>
                </div>
              </div>
              <CheckCircleIcon className="h-4 w-4 text-green-500" />
            </div>
          ) : (
            <div className="flex items-center text-gray-500">
              <CloudIcon className="h-4 w-4 mr-2" />
              <span>Selecione uma integração...</span>
            </div>
          )}
        </button>

        {isOpen && !disabled && (
          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
            {/* Opção "Nenhuma" */}
            <button
              type="button"
              onClick={() => handleSelect(null)}
              className="w-full text-left px-3 py-2 hover:bg-gray-50 border-b border-gray-100"
            >
              <div className="flex items-center text-gray-500">
                <span className="ml-6">Não usar integração</span>
              </div>
            </button>

            {/* Lista de integrações */}
            {integracoesAtivas.map((integracao) => (
              <button
                key={integracao.id}
                type="button"
                onClick={() => handleSelect(integracao.id)}
                className={`w-full text-left px-3 py-2 hover:bg-blue-50 transition-colors ${
                  selectedId === integracao.id ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CloudIcon className="h-4 w-4 text-blue-500" />
                    <div>
                      <div className="font-medium text-gray-900">
                        {integracao.nome}
                      </div>
                      <div className="flex items-center gap-1 text-sm text-gray-500">
                        <FolderIcon className="h-3 w-3" />
                        {integracao.pastaNome}
                        <span className="text-xs">
                          ({integracao.tipo === 'google_drive' ? 'Google Drive' : 
                            integracao.tipo === 'dropbox' ? 'Dropbox' : 'OneDrive'})
                        </span>
                      </div>
                    </div>
                  </div>
                  {selectedId === integracao.id && (
                    <CheckCircleIcon className="h-4 w-4 text-blue-500" />
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {selectedIntegracao && (
        <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded text-sm">
          <div className="flex items-center text-blue-800">
            <CloudIcon className="h-3 w-3 mr-1" />
            <span>
              Anexo será enviado para: <strong>{selectedIntegracao.pastaNome}</strong>
            </span>
          </div>
          <div className="text-xs text-blue-600 mt-1">
            📧 {selectedIntegracao.email}
          </div>
        </div>
      )}

      {/* Click outside to close */}
      {isOpen && (
        <div 
          className="fixed inset-0 z-5" 
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};

export default SeletorIntegracao;
